import type { AdminBookingRow } from '@/features/manage-bookings/types';

/**
 * Booking sales — the aggregate side of the booking business.
 *
 * From docs/booking-api.swagger.json (`BookingDashboardDto`). The number this
 * page exists to expose is "benefit value given": the report it replaces
 * counted purchases and sales only, so a booking paid with a membership perk
 * registered as zero everywhere and a class full of members looked like a class
 * that earned nothing.
 *
 * The individual bookings live on their own page now — this one answers what
 * happened in aggregate and which events it happened at.
 */

/** `BookingAttendanceStatsDto`. */
export interface BookingAttendance {
  totalBookings: number | null;
  confirmed: number | null;
  pending: number | null;
  cancelled: number | null;
  checkedIn: number | null;
  /** Counted over bookings whose timeslot has already started, not the range. */
  noShow: number | null;
  slots: number | null;
  timeslots: number | null;
  events: number | null;
  fillRate: number | null;
  vacancyRate: number | null;
  attendanceRate: number | null;
  uniqueCustomers: number | null;
  newCustomers: number | null;
}

/** `BookingPaymentStatsDto` — one count per `BookingPaymentCategory`. */
export interface BookingPaymentStats {
  paidOnline: number | null;
  paidOffline: number | null;
  usedBenefit: number | null;
  freeEvent: number | null;
  comped: number | null;
  awaitingPayment: number | null;
  refunded: number | null;
  /** The only figure here that means something is wrong. */
  needsReview: number | null;
}

/** `BookingMoneyStatsDto`. */
export interface BookingMoneyStats {
  collected: number | null;
  collectedOnline: number | null;
  collectedOffline: number | null;
  refunded: number | null;
  net: number | null;
  outstanding: number | null;
  /** List price of every booking paid with a perk — value given, never collected. */
  benefitValueRedeemed: number | null;
  averageTicket: number | null;
}

/** `BookingEventRollupDto`. */
export interface BookingEventRollup {
  eventId: string;
  eventName: string | null;
  eventImage: string | null;
  locationName: string | null;
  price: number | null;
  timeslots: number | null;
  slots: number | null;
  totalBookings: number | null;
  confirmed: number | null;
  pending: number | null;
  checkedIn: number | null;
  fillRate: number | null;
  vacancyRate: number | null;
  paid: number | null;
  usedBenefit: number | null;
  awaitingPayment: number | null;
  needsReview: number | null;
  collected: number | null;
  outstanding: number | null;
  benefitValueRedeemed: number | null;
}

/** `BookingDashboardDto`. */
export interface BookingDashboard {
  attendance: BookingAttendance | null;
  howItWasPaid: BookingPaymentStats | null;
  money: BookingMoneyStats | null;
  events: BookingEventRollup[] | null;
}

/**
 * `BookingTimeslotDto` — one timeslot of an event, with its bookings.
 *
 * Loaded on demand: the rollup deliberately does not carry timeslots, so
 * opening one event costs one request rather than every event paying for every
 * booking up front.
 */
export interface BookingTimeslot {
  id: string;
  startTime: string | null;
  endTime: string | null;
  maxCapacity: number | null;
  hostName: string | null;
  totalBookings: number | null;
  confirmed: number | null;
  pending: number | null;
  cancelled: number | null;
  checkedIn: number | null;
  fillRate: number | null;
  spotsLeft: number | null;
  paid: number | null;
  usedBenefit: number | null;
  awaitingPayment: number | null;
  needsReview: number | null;
  collected: number | null;
  outstanding: number | null;
  benefitValueRedeemed: number | null;
  bookings: AdminBookingRow[] | null;
}

/** The API sends fill rates as 0–1; every bar on this page wants 0–100, capped. */
export function fillPercent(rate: number | null | undefined) {
  return Math.min(Math.round((rate ?? 0) * 100), 100);
}
